"use client";

import { images } from "@/lib/images";

interface Recipe {
  level: "Facile" | "Confirmé";
  time: string;
  servings: number;
  chef: string;
  name: string;
  description: string;
  accent: string;
  origin: string;
  imageKey: keyof typeof images;
}

const RECIPES: Recipe[] = [
  {
    level: "Confirmé",
    time: "1h20",
    servings: 6,
    chef: "Chef Aminata Faye",
    name: "Thieboudienne Royal",
    description:
      "Le plat emblématique dans sa version la plus raffinée : riz brisé doré au safran, poisson de mer lutté, légumes du terroir et rof parfumé.",
    accent: "#c87b4a",
    origin: "Sénégal ·_signature",
    imageKey: "recipe:thieboudienne",
  },
  {
    level: "Facile",
    time: "50 min",
    servings: 4,
    chef: "Chef Mariama Sow",
    name: "Yassa Poulet aux Olives",
    description:
      "Poulet fermier mariné, oignons confits au citron confit, olives de Provence. Servi sur riz parfumé ou semoule de mil.",
    accent: "#7d9b4e",
    origin: "Sénégal · Provence",
    imageKey: "recipe:yassa",
  },
  {
    level: "Facile",
    time: "35 min",
    servings: 4,
    chef: "Chef Pierre Lemoine",
    name: "Fondant Chocolat Or",
    description:
      "Fondant au chocolat noir 70% doré à la feuille d'or, framboises fraîches et glace vanille de Madagascar. La signature sucrée de la maison.",
    accent: "#6e1f2a",
    origin: "France · signature",
    imageKey: "recipe:fondant",
  },
];

// Shared arrow icon used on both per-card and section-level CTAs.
function ArrowRight({ className = "" }: { className?: string }) {
  return (
    <svg
      className={className}
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 12h14"></path>
      <path d="m12 5 7 7-7 7"></path>
    </svg>
  );
}

export function RecipesSection() {
  return (
    <section
      id="recipes"
      className="relative bg-cream py-16 md:py-20 border-t border-ink/8"
    >
      <div className="mx-auto max-w-7xl px-6">
        {/* Header — title on the left, primary CTA on the right (visible on arrival) */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div className="max-w-2xl">
            <div className="ornament-line mb-4">
              <span className="text-gold text-[11px] tracking-[0.35em] uppercase">
                Recettes &amp; Art de vivre
              </span>
            </div>
            <h2 className="font-display text-ink text-4xl md:text-5xl leading-[1.05]">
              La table{" "}
              <span className="gold-text italic">d&apos;Hyper OR.</span>
            </h2>
            <p className="mt-4 text-ink/65 font-serif text-base md:text-lg leading-relaxed">
              Chaque mois, nos chefs partagent leurs recettes signature.
              Produits du terroir sublimés, gestes précis, présentation digne
              d&apos;un grand restaurant.
            </p>
          </div>

          {/* Primary CTA — visible the moment the section enters the viewport */}
          <a
            href="#la-table"
            className="inline-flex shrink-0 items-center gap-3 px-7 py-3.5 rounded-full gold-gradient text-ink font-semibold tracking-[0.2em] uppercase text-xs md:text-sm gold-shadow hover:scale-[1.02] active:scale-[0.98] transition-transform self-start md:self-auto"
          >
            Rejoindre le Cercle OR
            <ArrowRight />
          </a>
        </div>

        {/* Recipes grid */}
        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {RECIPES.map((r, idx) => {
            const imgSrc = images[r.imageKey] as string | undefined;
            return (
              <article
                key={idx}
                className="group relative bg-white border border-ink/8 rounded-2xl overflow-hidden hover:luxury-shadow transition-all duration-500 hover:-translate-y-1 flex flex-col"
              >
                {/* Visual — shorter 16/10 aspect frees vertical space for the body */}
                <div
                  className="relative aspect-[16/10] overflow-hidden"
                  style={{
                    background: `linear-gradient(135deg, ${r.accent}33 0%, ${r.accent}10 60%, var(--cream) 100%)`,
                  }}
                >
                  {imgSrc && (
                    <img
                      src={imgSrc}
                      alt={r.name}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                  {/* Level badge */}
                  <div className="absolute top-3 left-3 flex flex-col gap-2">
                    <span className="px-2.5 py-1 rounded-full text-[9px] tracking-[0.18em] uppercase font-semibold bg-ink text-cream">
                      {r.level}
                    </span>
                  </div>
                  {/* Time + servings */}
                  <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                    <span className="px-2 py-0.5 rounded-full text-[10px] tracking-[0.15em] uppercase font-medium bg-white/90 text-ink/70 backdrop-blur flex items-center gap-1">
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="M12 6v6l4 2"></path>
                      </svg>
                      {r.time}
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-[10px] tracking-[0.15em] uppercase font-medium bg-white/90 text-ink/70 backdrop-blur flex items-center gap-1">
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                        <circle cx="9" cy="7" r="4"></circle>
                        <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                      </svg>
                      {r.servings}
                    </span>
                  </div>
                </div>

                {/* Body — tighter padding so the CTA button is visible higher up */}
                <div className="flex-1 flex flex-col p-5">
                  <span className="text-[10px] tracking-[0.25em] uppercase text-gold font-medium">
                    {r.chef}
                  </span>
                  <h3 className="mt-1.5 font-display text-ink text-xl md:text-2xl leading-tight">
                    {r.name}
                  </h3>
                  <p className="mt-2 text-ink/60 text-[13px] leading-relaxed font-serif">
                    {r.description}
                  </p>
                  <p className="mt-1.5 text-ink/40 text-[10px] tracking-[0.2em] uppercase">
                    {r.origin}
                  </p>

                  {/* Promoted "Voir la recette" — now a gold-outline pill button, full-width */}
                  <a
                    href="#la-table"
                    className="mt-4 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full border border-gold/60 text-gold hover:bg-gold hover:text-ink hover:border-gold transition-all text-[10px] tracking-[0.25em] uppercase font-semibold"
                  >
                    Voir la recette
                    <ArrowRight />
                  </a>
                </div>
              </article>
            );
          })}
        </div>

        {/* Closing CTA — kept at bottom for users who scroll to the end */}
        <div className="mt-10 flex justify-center">
          <a
            href="#la-table"
            className="inline-flex items-center gap-3 px-7 py-3.5 rounded-full bg-ink text-cream font-semibold tracking-[0.2em] uppercase text-xs md:text-sm hover:bg-ink/90 hover:scale-[1.02] active:scale-[0.98] transition-all border border-gold/30"
          >
            Rejoindre le Cercle OR
            <ArrowRight />
          </a>
        </div>
      </div>
    </section>
  );
}
